"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as LucideIcons from "lucide-react";
import { getMobileTabItems, ADMIN_NAV_ITEMS } from "@/config/adminNav";
import { MEMBERS_ENABLED } from "@/lib/featureFlags";
import { resolveActiveNavId } from "./adminShellHelpers";
import useFocusTrap from "./hooks/useFocusTrap";

// Mobile bottom tab bar — visible only < 768px.
// Slot 4 ("more") opens a bottom sheet listing all non-tab items.
export default function AdminTabBar() {
  const pathname = usePathname();
  const activeId = resolveActiveNavId(pathname);
  const [sheetOpen, setSheetOpen] = useState(false);
  const tabs = getMobileTabItems();
  const moreItems = ADMIN_NAV_ITEMS.filter(
    (i) => !i.mobileTab && (!i.memberOnly || MEMBERS_ENABLED),
  );

  const sheetRef = useFocusTrap({
    active: sheetOpen,
    onEscape: () => setSheetOpen(false),
  });

  return (
    <>
      <nav className="admin-tabbar" aria-label="底部快捷導覽">
        {tabs.map((tab) => {
          const Icon = LucideIcons[tab.icon] || LucideIcons.Circle;
          const isActive = tab.id === activeId;
          const isMore = tab.id === "more";

          if (isMore) {
            return (
              <button
                key={tab.id}
                type="button"
                className="admin-tabbar__item"
                onClick={() => setSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={sheetOpen}
              >
                <Icon size={22} aria-hidden="true" />
                <span className="admin-tabbar__label">{tab.label}</span>
              </button>
            );
          }

          return (
            <Link
              key={tab.id}
              href={tab.href}
              className={`admin-tabbar__item ${isActive ? "is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon size={22} aria-hidden="true" />
              <span className="admin-tabbar__label">{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      {sheetOpen && (
        <div
          className="admin-tabbar-sheet-backdrop"
          onClick={() => setSheetOpen(false)}
          role="presentation"
        >
          <div
            ref={sheetRef}
            className="admin-tabbar-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="所有功能"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-tabbar-sheet__handle" aria-hidden="true" />
            <h2 className="admin-tabbar-sheet__title">所有功能</h2>
            <ul className="admin-tabbar-sheet__list">
              {moreItems.map((item) => {
                const Icon = LucideIcons[item.icon] || LucideIcons.Circle;
                return (
                  <li key={item.id}>
                    <Link
                      href={item.href}
                      className="admin-tabbar-sheet__row"
                      onClick={() => setSheetOpen(false)}
                    >
                      <Icon size={20} aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
